<template>
  <div class="admin-user-list px-2 px-md-4 py-2 py-md-4">
    <ConveyTable
      showSearch
      :showCreate="$permission('create:user')"
      tableClassName="admin-user-table"
      :items="users"
      :fields="fields"
      :placeholders="placeholders"
      :perPageOptions="perPageOptions"
      :perPage="perPage"
      :currentPage="currentPage"
      :totalRows="totalRows"
      :isLoading="isFetchingUsers"
      @onFetch="(options) => fetchUsers(options)"
      @onCreate="addUser"
    >
      <template #create-icon>
        <b-icon icon="person-plus"></b-icon>
      </template>
      <template #create-label>
        Add User
      </template>
      <template #cell(fullName)="data">
        <router-link
          v-if="$permission('read:user')"
          :to="`/admin/users/${data.item.user_id}`"
        >
          {{ data.value }}
        </router-link>
        <template v-else>{{ data.value }}</template>
      </template>
      <template #cell(roles)="data">
        <span
          v-for="role in data.value"
          :key="`${data.item.user_id}_${role}`"
          :class="`${getRoleType(role)} badge font-weight-medium mr-2 text-capitalize`"
        >
          {{ getRoleLabel(role) }}
        </span>
      </template>
      <template #cell(status)="data">
        <span
          :class="`${data.value === 'Active' ? 'active' : 'suspended'} badge font-weight-medium mr-2 text-capitalize`"
        >
          {{ data.value }}
        </span>
      </template>
    </ConveyTable>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";
import formatter from "@/mixins/formatter";
import errorHandler from "@/mixins/errorHandler";
import ConveyTable from "@/components/ConveyTable";

export default {
  name: "AdminUserList",
  mixins: [formatter, errorHandler],
  components: { ConveyTable },
  data() {
    return {
      users: [],
      perPage: 10,
      perPageOptions: [5, 10, 25, 50, 100],
      availableRoles: [],
      currentPage: 1,
      totalRows: 0,
      isFetchingUsers: false,
      placeholders: {
        search: "Search name or email..."
      },
      fields: [
        { key: "fullName", label: "Name", sortable: false },
        { key: "email", label: "Email", sortable: false },
        { key: "provider", label: "Provider", sortable: false },
        { key: "roles", label: "Role", sortable: false },
        { key: "status", label: "Status", sortable: false },
        { key: "lastLogin", label: "Last Login", sortable: false },
      ],
      fetchAbortController: null,
    };
  },
  computed: {
    ...mapGetters("agency", ["currentAgencyId", "currentAgency"]),
  },
  created() {
    try {
      this.availableRoles = this.currentAgency.roles.map(r => ({ ...r, text: r.role, value: r.role_id }));
    } catch (err) { }
  },
  methods: {
    abortFetchOperation() {
      if (this.fetchAbortController) {
        this.fetchAbortController.abort();
        this.fetchAbortController = null;
      }
    },
    async fetchUsers(fetchOptions) {
      try {
        this.abortFetchOperation();
        this.fetchAbortController = new AbortController();
        const {
          currentPage,
          perPage,
          searchKey,
        } = fetchOptions;

        this.currentPage = currentPage;
        this.perPage = perPage;
        this.isFetchingUsers = true;

        const offset = (currentPage - 1) * perPage;
        const params = {
          offset,
          limit: perPage,
        };
        if (searchKey) {
          params['search'] = searchKey; 
        }

        const resp = await this.$ConveyApi.usersApi.getUsersAssignedToAgency({
          agencyId: this.currentAgencyId,
          params,
          signal: this.fetchAbortController.signal,
        });
        const { data, metadata } = resp.data;
        this.users = data.map((item) => {
          const { roles, fname, lname, last_login, federated_id, suspended } = item;
          return {
            ...item,
            fullName: `${fname} ${lname}`,
            roles: roles ? roles.split(',') : [],
            lastLogin: last_login ? this.formatDate(last_login, "MMM D, YYYY h:mm a") : "",
            provider: this.getProvider(federated_id),
            status: suspended ? 'Suspended' : 'Active'
          };
        });
        this.totalRows = metadata ? metadata.totalRows || 0 : 0;
      } catch (err) {
        if (!this.isApiCanceled(err)) {
          console.log("[AdminUserList:fetchUsers] ERROR: ", err);
        }
      } finally {
        this.isFetchingUsers = false;
      }
    },
    getRoleType(roleId) {
      const role = this.availableRoles.find(item => item.value === roleId);
      if (role) {
        const roleText = role.text.toLowerCase();
        if (roleText.includes('admin')) {
          return 'admin';
        } else if (roleText.includes('operator')) {
          return 'operator';
        } else {
          return 'other';
        }
      } else {
        return 'unknown'
      }
    },
    getRoleLabel(roleId) {
      const role = this.availableRoles.find(item => item.value === roleId);
      if (role) {
        return role.text;
      } else {
        return 'Unknown'
      }
    },
    getProvider(federatedId) {
      if (federatedId.startsWith('auth0')) {
        return 'Convey911';
      } else if (federatedId.startsWith('oauth2')) {
        const federatedIdSplits = federatedId.split('|');
        if (federatedIdSplits.length > 1) {
          return federatedIdSplits[1];
        }
      }
      return '';
    },
    addUser() {
      this.$router.push({ name: "Admin Create User" }).catch(() => {});
    }
  },
  beforeDestroy() {
    this.abortFetchOperation();
  }
};
</script>

<style lang="scss">
.admin-user-list {
  width: 100%;
  height: 100%;

  .admin-user-table {
    span.badge.admin,
    span.badge.suspended {
      background-color: #f3ced6 !important;
      color: #ef5350 !important;
    }

    span.badge.operator,
    span.badge.active {
      background-color: #b7f1e0 !important;
      color: #0acc95 !important;
    }

    span.badge.other {
      background-color: #cbe2ff !important;
      color: #186dde !important;
    }

    span.badge.unknown {
      background-color: #cbe2ff !important;
      color: #186dde !important;
    }
  }
}
</style>
